import { useEffect, useMemo, useState } from 'react';
import {
  PlatformTopBar,
  ThemeSyncScript,
  getDefaultApps,
  getThemeCookie,
  setThemeCookie,
} from '@mayline-ai/platform-nav';
import type { ThemeMode, UserInfo, Organization } from '@mayline-ai/platform-nav';
import { sessionToNavProps } from '@mayline-ai/platform-auth';
import type { PlatformSession } from '@mayline-ai/platform-auth';
import { useAuth } from '../auth/ParsleeAuthProvider';

/**
 * Shared Parslee platform top bar for the admin dashboard (U4).
 *
 * Reads the app session from `useAuth()` (U2), normalizes it to a C5
 * `PlatformSession`, and runs it through the shared one-way `sessionToNavProps`
 * adapter to get the identity / active-org / org-switcher / linked-account
 * display props, which are spread into the published `@mayline-ai/platform-nav`
 * `PlatformTopBar`. All behaviour (logout, org/account switch, theme) is
 * hand-wired here — the adapter is pure data, no callbacks (SC boundary).
 *
 * The admin dashboard is NOT a product in the app switcher: `getDefaultApps()`
 * returns chat/crm/documents/studio/trainer (no dashboard entry). `currentApp`
 * is `'dashboard'` for context only — since that id is absent from the apps
 * list, no tab is highlighted and the mobile current-app label renders nothing.
 */

// sessionToNavProps yields `null` for user/org on a partial session. The bar only
// renders on gated (authenticated) routes, so these fallbacks are effectively
// unreachable — they exist only to satisfy the non-null PlatformTopBar props.
const FALLBACK_USER: UserInfo = { name: 'User', email: '', initials: 'U' };
const FALLBACK_ORG: Organization = { id: 'org_button_up', name: 'ButtonUp' };

export function PlatformTopBarWrapper() {
  const {
    user,
    organizations,
    linkedAccounts,
    isAuthenticated,
    logout,
    switchOrg,
    addAccount,
    switchToAccount,
  } = useAuth();

  const [theme, setTheme] = useState<ThemeMode>(
    () => (getThemeCookie() as ThemeMode | null) ?? 'light',
  );

  // A saved dark choice applies on load; the toggle below only updated the class on change.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const nav = useMemo(() => {
    const activeOrgName = organizations.find((o) => o.id === user?.organizationId)?.name;
    const session: PlatformSession = {
      authenticated: isAuthenticated,
      account: user ? { id: user.id, email: user.email, name: user.name } : undefined,
      activeOrganization: user?.organizationId,
      organizationName: activeOrgName ?? 'ButtonUp',
      organizations,
      linkedAccounts,
      linkedAccountsStatus: 'healthy',
      // C5 compatibility-window fields — not tracked by the admin dashboard session.
      sessionFamilyId: null,
      familyStatus: 'unknown',
      appSessionId: null,
    };
    return sessionToNavProps(session);
  }, [user, organizations, linkedAccounts, isAuthenticated]);

  const apps = useMemo(() => getDefaultApps(), []);

  return (
    <>
      <ThemeSyncScript />
      <PlatformTopBar
        currentApp="dashboard"
        user={nav.user ?? FALLBACK_USER}
        organization={nav.organization ?? FALLBACK_ORG}
        organizations={nav.organizations}
        linkedAccounts={nav.linkedAccounts}
        onLogout={() => void logout()}
        onSwitchOrg={(orgId) => void switchOrg(orgId)}
        onAddAccount={addAccount}
        onSwitchToAccount={switchToAccount}
        theme={theme}
        onThemeChange={(t) => { setTheme(t); setThemeCookie(t); }}
        apps={apps}
      />
    </>
  );
}

export default PlatformTopBarWrapper;
